import { useEffect, useRef, useState } from 'react'
import { PartialBrief } from '../Types/Brief'
import { SPACE_RECOMMENDATION_SERVICE } from '../Constants'

export interface Space {
  id: string
  title: string
  venueName: string
  location: {
    city: string
    country: string
    latlng: { lat: number; lng: number }
  }
  images: string[]
  description: string
  capacity: number
  hostID: number
  googleRating: number
  googleReviews: number
  tags: string[]
}

export default function useSpaceRecommendations(brief: PartialBrief): {
  loading: boolean
  recommendations: Space[]
} {
  const [loading, setLoading] = useState<boolean>(true)
  const [recommendations, setRecommendations] = useState<Space[]>([])
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }

    timeoutRef.current = setTimeout(async () => {
      let retries = 0
      const fetchRecs = async (
        radius: number,
        textMatchThreshold?: number,
        imageMatchThreshold?: number
      ): Promise<void> => {
        let prompt = `
          Find spaces which meet the following criteria:
            - Suitable for ${brief.collectionName} events; and
            - The room should support ${brief.capacities} people, in a ${brief.layouts} layout.
          It is also important that:       
        `

        if (brief.additionalNotes) {
          prompt += `
          - ${brief.additionalNotes}
        `
        }
        if (brief.budgetNotes) {
          prompt += `
          - ${brief.budgetNotes}
        `
        }
        if (brief.cateringNeeds) {
          prompt += `
          - ${brief.cateringNeeds}
        `
        }

        const data = await fetch(SPACE_RECOMMENDATION_SERVICE, {
          method: 'POST',
          body: JSON.stringify({
            filters: {
              location: {
                lat: brief.lat,
                lon: brief.lon,
                radius,
              },
              eventType: brief.collection,
              essentialFacilities: brief.requiredSpaceFacilityIds,
            },
            cleanPrompt: prompt,
            textMatchThreshold,
            imageMatchThreshold,
          }),
          headers: {
            'Content-Type': 'application/json',
          },
        })

        const { spaces } = await data.json()

        if (spaces.length < 10) {
          if (retries === 0) {
            retries++
            return await fetchRecs(3000, 0.825, 0.825)
          }
          if (retries === 1) {
            retries++
            return await fetchRecs(5000, 0.8, 0.8)
          }
        } else {
          retries = 0
          setRecommendations(spaces as Space[])
          setLoading(false)
        }
      }
      fetchRecs(3000)
    }, 500)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [brief])

  return { loading, recommendations }
}
